import { Canvas, FabricImage, Rect } from "fabric";
import { StoreName } from "../../decorators/StoreNameDecorator";
import API from "../../models/API";
import { DownloadedImage } from "../../models/DownloadedImage";
import { SpanHighlightObject } from "../../models/HighlightObject";
import { AbstractGenericStore, GenericStore } from "../GenericStoreClass";
import type Page from "../../models/Page";

type DownloadedImageWithSpans = DownloadedImage & {
  spanCount?: number;
};
class ImageStoreClass extends AbstractGenericStore<
  string,
  DownloadedImageWithSpans
> {
  // public lookup(key: string): Promise<DownloadedImage | undefined> {
  //     throw new Error("Method not implemented.");
  // }

  async lookup(url: string): Promise<DownloadedImageWithSpans | undefined> {
    try {
      console.log(`ImageStore.lookup - ${url}`);
      const response = await API.get(url);
      if (!response.ok) return undefined;

      const blob = await response.blob();

      return new DownloadedImage(url, blob) as DownloadedImageWithSpans;
    } catch (e) {
      console.log(e);
      return undefined;
    }
  }
  storedObjectKeysEquals(a: string, b: string): boolean {
    return a === b;
  }
}

@StoreName("DownloadedImageStore")
class DownloadedImageStoreClass<
  _,
  __ extends DownloadedImage,
> implements GenericStore<string, DownloadedImage> {
  protected images = new ImageStoreClass();
  protected imagesWithSpans = new ImageStoreClass();
  private spanCanvas: HTMLCanvasElement | undefined;
  private fabricCanvas: Canvas | undefined;
  private _init: boolean = true;

  constructor() {
    this.images = new ImageStoreClass();
    this.imagesWithSpans = new ImageStoreClass();
  }

  setup() {
    if (this._init) {
      console.log("Init downloaded image store");
      // create side canvas
      this.spanCanvas = document.createElement("canvas");
      this.spanCanvas.id = "spanCanvas-" + Math.random() + "" + Math.random();
      this.spanCanvas.style.display = "none";
      console.log("Span canvas at " + this.spanCanvas.id);
      document.body.append(this.spanCanvas);
      this.fabricCanvas = new Canvas(this.spanCanvas.id);
      // Hacky, hide wrapper canvas from page
      document.getElementById(
        this.spanCanvas.id!,
      )!.parentElement!.style.display = "none";
      // this.fabricCanvas.wr.style.display = 'none';
      // hide fabric canvas from DOM
      this._init = false;
    }
  }

  storedObjectKeysEquals(a: string, b: string): boolean {
    return a === b;
  }

  get(id: string): Promise<DownloadedImage | undefined> {
    throw new Error("DownloadedImageStore.get not implemented.");
  }
  getMany(id: string[]): Promise<DownloadedImage | undefined>[] {
    throw new Error("DownloadedImageStore.getMany not implemented.");
  }
  getAll(): DownloadedImage[] {
    throw new Error("DownloadedImageStore.getAll not implemented.");
  }
  setMany(values: DownloadedImage[], overwrite: boolean): DownloadedImage[] {
    throw new Error("DownloadedImageStore.setMany not implemented.");
  }
  set(value: DownloadedImage, overwrite?: boolean): DownloadedImage {
    throw new Error("DownloadedImageStore.set not implemented.");
  }

  // TODO: create canvas, load image, render spans, save to blob
  // TODO: should this return image with modified url in same store, or new store ? latter for now

  renderSpans(
    image: DownloadedImage,
    spans: SpanHighlightObject[],
    pageDimensions?: {
      width?: number;
      height?: number;
    },
  ): Promise<DownloadedImageWithSpans> {
    return new Promise((res, rej) => {
      console.log(`renderSpans()`);
      console.log(image);
      this.fabricCanvas?.clear();
      // set spans on canvas
      FabricImage.fromURL(image.objectUrl).then((img) => {
        this.fabricCanvas?.setDimensions({
          width: img.width,
          height: img.height,
        });

        this.fabricCanvas!.backgroundImage = img;

        // Sometimes the image dimensions than the
        // dimensions of the page, based on some extraction logic
        // before the data is sent to Accelerator
        // account for this by adjusting the position
        // of the spans
        const xRatio = pageDimensions?.width
          ? img.width / pageDimensions.width
          : 1;
        const yRatio = pageDimensions?.height
          ? img.height / pageDimensions.height
          : 1;

        for (const span of spans) {
          const rect = new Rect({
            // dimensions
            height: span.height * yRatio,
            width: span.width * xRatio,
            // positioning
            left: span.x * xRatio,
            top: span.y * yRatio,
            originX: "left",
            originY: "top",
            // styling
            fill: "transparent",
            stroke: span.color,
            strokeWidth: 2,
          });
          this.fabricCanvas!.add(rect);
        }
        console.log(`final renderAll()`);
        this.fabricCanvas!.renderAll();
        // Convert canvas to downloadedImage
        console.log(`converting fabric canvas to blob`);
        const newImage = new DownloadedImage(
          image.storeId,
          image.blob,
        ) as DownloadedImageWithSpans;
        this.fabricCanvas!.toCanvasElement().toBlob((x) => {
          if (x == null) {
            return rej("Could not render canvas to blob");
          }
          newImage.blob = x;
          newImage.spanCount = spans.length;
          res(newImage);
        });
      });
    });
  }

  async getWithRenderedSpans(
    id: string,
    inclSpans: boolean,
    spans: SpanHighlightObject[],
    pageDimensions?: {
      width: number;
      height: number;
    },
  ): Promise<DownloadedImage | undefined> {
    console.log(`getRenderedWithSpans(${id},${inclSpans},${spans.length}[])`);
    // load image
    let image = await this.images.get(id);
    if (!image) {
      console.log(`normal image undefined, returning undefined`);
      return undefined;
    }
    // render spans
    if (inclSpans) {
      console.log(`rendering with spans`);
      image = this.imagesWithSpans.set(
        await this.renderSpans(image, spans, pageDimensions),
      );
    } else {
      console.log("rendering without spans");
    }
    return image;
  }
  public lookup(key: string): Promise<DownloadedImage | undefined> {
    throw new Error("Method not implemented.");
  }
  // async lookup(url: string): Promise<DownloadedImage | undefined> {
  //     try {
  //         const response = await API.get(url);
  //         if (!response.ok) return undefined;

  //         const blob = await response.blob();

  //         return new DownloadedImage(url, blob);
  //     }
  //     catch (e) {
  //         console.log(e);
  //         return undefined;
  //     }
  // }
}

const DownloadedImageStore = new DownloadedImageStoreClass();

export { DownloadedImageStore, ImageStoreClass };
